
import React from 'react';
import { cn } from '@/lib/utils';
import { CustomSelectProvider } from './CustomSelectContext';
import { CustomSelectTrigger } from './CustomSelectTrigger';
import { CustomSelectValue } from './CustomSelectValue';
import { CustomSelectContent } from './CustomSelectContent';
import { CustomSelectItem } from './CustomSelectItem';
import useClickOutside from '@/hooks/useClickOutside';

interface CustomSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onValueChange,
  onOpenChange,
  children,
  className
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const handleValueChange = (newValue: string) => {
    onValueChange(newValue);
    handleOpenChange(false);
  };

  // Use click outside to close dropdown
  const containerRef = useClickOutside(() => {
    if (isOpen) {
      handleOpenChange(false);
    }
  });

  // Clone children to pass triggerRef to CustomSelectContent
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === CustomSelectTrigger) {
        return React.cloneElement(child, { ref: triggerRef } as any);
      }
      if (child.type === CustomSelectContent) {
        return React.cloneElement(child, { triggerRef } as any);
      }
    }
    return child;
  });

  return (
    <CustomSelectProvider 
      value={value} 
      onValueChange={handleValueChange} 
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
    >
      <div ref={containerRef} className={cn("relative", className)}>
        {childrenWithProps}
      </div>
    </CustomSelectProvider>
  );
};

export {
  CustomSelect,
  CustomSelectTrigger,
  CustomSelectValue,
  CustomSelectContent,
  CustomSelectItem
};
